import { Box, BoxProps, Theme } from "@mui/material";
import { SxProps } from "@mui/system/styleFunctionSx";
import clsx from "clsx";
import React, { Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useMemo, useState } from "react";

type ItemSetStylingContextValue =
  | { setActiveItems: Dispatch<SetStateAction<string[]>>; setActiveClasses: Dispatch<SetStateAction<string[]>> }
  | undefined;
const ItemSetStylingContext = React.createContext<ItemSetStylingContextValue>(undefined);
ItemSetStylingContext.displayName = "ItemSetStyling";

interface SetStylesContextValue {
  styleNames: string[];
  negativeStylesNames: string[];
}
const SetStylesContext = React.createContext<SetStylesContextValue>({ styleNames: [], negativeStylesNames: [] });
SetStylesContext.displayName = "SetStyles";

const classPrefix = "ItemSetStyling";

const getRootClassName = (styleName: string) => `${classPrefix}-root-class-${styleName}`;
const getRootItemName = (item: string) => `${classPrefix}-root-item-${item}`;
const getItemClassName = (styleName: string) => `${classPrefix}-item-class-${styleName}`;
const getItemName = (item: string) => `${classPrefix}-item-${item}`;

const getClassSelector = (styleName: string) => `&.${getRootClassName(styleName)} .${getItemClassName(styleName)}`;
const getItemSelector = (styleName: string, items: string[]) =>
  items.map((item) => `&.${getRootClassName(styleName)}.${getRootItemName(item)} .${getItemName(item)}`).join(", ");

type ItemSetStylingProviderProps = PropsWithChildren<
  {
    itemStyles: Record<string, SxProps<Theme>>;
    activeStyles?: Record<string, SxProps<Theme>>;
  } & BoxProps
>;

export const ItemSetStylingProvider = ({
  children,
  itemStyles,
  activeStyles,
  ...props
}: ItemSetStylingProviderProps) => {
  const [activeItems, setActiveItems] = useState<string[]>([]);
  const [activeClasses, setActiveClasses] = useState<string[]>([]);

  const activeStylesSxDef = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(activeStyles ?? {}).map(([styleName, style]) => [getClassSelector(styleName), style])
      ),
    [activeStyles]
  );

  const itemStylesSxDef = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(itemStyles ?? {}).map(([itemClassName, itemStyle]) => [
          getItemSelector(itemClassName, activeItems),
          itemStyle,
        ])
      ),
    [activeItems, itemStyles]
  );

  const rootClasses = useMemo(() => {
    const activatedItems = activeItems.map((element) => getRootItemName(element));
    const activatedClasses = activeClasses.flatMap((element) => getRootClassName(element));
    return [...activatedItems, ...activatedClasses];
  }, [activeItems, activeClasses]);

  const fullStyles = useMemo(
    () => ({ ...activeStylesSxDef, ...itemStylesSxDef, ...props.sx }),
    [activeStylesSxDef, itemStylesSxDef, props.sx]
  );

  const itemStyleNames = useMemo(() => Object.keys(itemStyles), [itemStyles]);
  const activeStyleNames = useMemo(() => Object.keys(activeStyles ?? {}), [activeStyles]);
  const itemSetStylingContextValue = useMemo(() => ({ setActiveItems, setActiveClasses }), []);
  const setStylesContextValue = useMemo(
    () => ({ styleNames: itemStyleNames, negativeStylesNames: activeStyleNames }),
    [itemStyleNames, activeStyleNames]
  );

  return (
    <ItemSetStylingContext.Provider value={itemSetStylingContextValue}>
      <SetStylesContext.Provider value={setStylesContextValue}>
        <Box {...props} className={clsx(rootClasses)} sx={fullStyles}>
          {children}
        </Box>
      </SetStylesContext.Provider>
    </ItemSetStylingContext.Provider>
  );
};

export const useRegisterForItemSetStyling = (id: string) => {
  const context = useContext<SetStylesContextValue>(SetStylesContext);
  if (!context) {
    throw new Error("useRegisterForItemSetStyling must be used within a ItemSetStylingProvider");
  }
  const styleNames = context.styleNames.map((element) => getItemClassName(element));
  return [...styleNames, getItemName(id)].join(" ");
};

export const useItemSetStyling = () => {
  const context = useContext<ItemSetStylingContextValue>(ItemSetStylingContext);
  if (!context) {
    throw new Error("useItemSetStyling must be used within a ItemSetStylingProvider");
  }
  const { setActiveClasses, setActiveItems } = context;

  const addActiveItem = useCallback(
    (item: string) => {
      setActiveItems((prev) => [...(prev ?? []), item]);
    },
    [setActiveItems]
  );

  const removeActiveItem = useCallback(
    (item: string) => {
      setActiveItems((prev) => (prev ?? []).filter((c) => c !== item));
    },
    [setActiveItems]
  );

  const replaceActiveItem = useCallback(
    (item: string[]) => {
      setActiveItems(item);
    },
    [setActiveItems]
  );

  const clearActiveItems = useCallback(() => {
    setActiveItems([]);
  }, [setActiveItems]);

  const addActiveClass = useCallback(
    (className: string) => {
      setActiveClasses((prev) => [...(prev ?? []), className]);
    },
    [setActiveClasses]
  );

  const removeActiveClass = useCallback(
    (className: string) => {
      setActiveClasses((prev) => (prev ?? []).filter((c) => c !== className));
    },
    [setActiveClasses]
  );

  const replaceActiveClass = useCallback(
    (newClasses: string[]) => {
      setActiveClasses(newClasses);
    },
    [setActiveClasses]
  );

  const clearActiveClasses = useCallback(() => {
    setActiveClasses([]);
  }, [setActiveClasses]);

  return {
    addActiveItem,
    removeActiveItem,
    replaceActiveItem,
    clearActiveItems,
    addActiveClass,
    removeActiveClass,
    replaceActiveClass,
    clearActiveClasses,
  };
};
